import { isTrustedPreviewRuntime } from '@/shared/env/runtimeMode';
import {
  publishRuntimeResponse,
  runtimeFetch,
} from '@/shared/runtime/runtimeRequestState';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim() || 'https://api.upmini.app';
const STORAGE_KEY = 'showcase-runtime-auth';

type AuthTokens = {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  telegram_id?: string | number;
  max_id?: string | number;
  user_id?: string;
  project_id?: string;
};

export type RuntimeMessengerUser = {
  id?: string | number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

let memoryTokens: AuthTokens | null = null;
let reauthentication: Promise<AuthTokens> | null = null;

function readStoredTokens(): AuthTokens | null {
  if (memoryTokens) return memoryTokens;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    memoryTokens = raw ? (JSON.parse(raw) as AuthTokens) : null;
  } catch {
    memoryTokens = null;
  }
  return memoryTokens;
}

function storeTokens(tokens: AuthTokens): void {
  const safeTokens = { ...tokens, refresh_token: undefined };
  memoryTokens = safeTokens;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safeTokens));
  } catch {
    // The in-memory token remains usable when storage is unavailable.
  }
}

export function getAccessToken(): string | null {
  return readStoredTokens()?.access_token || null;
}

export function getAuthenticatedTelegramId(): string | null {
  const value = readStoredTokens()?.telegram_id;
  return value === undefined || value === null ? null : String(value);
}

export function getAuthenticatedMaxId(): string | null {
  const value = readStoredTokens()?.max_id;
  return value === undefined || value === null ? null : String(value);
}

export function getAuthenticatedUserId(): string | null {
  return readStoredTokens()?.user_id || null;
}

export function getAuthenticatedIdentityId(): string | null {
  return getAuthenticatedUserId() || getAuthenticatedTelegramId() || getAuthenticatedMaxId();
}

export function getRuntimeMessengerUser(): RuntimeMessengerUser | undefined {
  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  if (telegramUser) return telegramUser;
  return (window as Window & {
    WebApp?: { initDataUnsafe?: { user?: RuntimeMessengerUser } };
  }).WebApp?.initDataUnsafe?.user;
}

export async function authenticateShowcase(projectId: string, signal?: AbortSignal): Promise<AuthTokens> {
  const maxWebApp = (window as Window & { WebApp?: { initData?: string } }).WebApp;
  const provider = maxWebApp?.initData ? 'max' : 'telegram';
  const initData = provider === 'max' ? maxWebApp?.initData || '' : window.Telegram?.WebApp?.initData || '';
  if (!initData) {
    if (isTrustedPreviewRuntime) {
      const existing = readStoredTokens();
      if (existing?.access_token) return existing;
    }
    throw new Error('Не удалось получить данные запуска Telegram или MAX');
  }

  const endpoint = provider === 'max'
    ? import.meta.env.VITE_MAX_AUTH_ENDPOINT?.trim() || `${API_BASE_URL}/auth/max`
    : `${API_BASE_URL}/auth/telegram`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ init_data: initData, project_id: projectId, provider }),
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Не удалось авторизоваться: HTTP ${response.status}`);
  }
  const tokens = {
    ...((await response.json()) as AuthTokens),
    project_id: projectId,
  };
  if (!tokens.access_token) {
    throw new Error('Сервис авторизации не вернул access token');
  }
  storeTokens(tokens);
  return tokens;
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = String(init.method || 'GET').toUpperCase();
  const token = getAccessToken();
  const headers = new Headers(init.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await runtimeFetch(
    input,
    { ...init, headers, credentials: init.credentials ?? 'include' },
    method,
  );
  const hasLaunchData = Boolean(
    window.Telegram?.WebApp?.initData
    || (window as Window & { WebApp?: { initData?: string } }).WebApp?.initData,
  );
  if (response.status !== 401 || !hasLaunchData) {
    await publishRuntimeResponse(response, method);
    return response;
  }

  const projectId = readStoredTokens()?.project_id || '';
  if (!projectId) {
    await publishRuntimeResponse(response, method);
    return response;
  }

  reauthentication ??= authenticateShowcase(projectId).finally(() => {
    reauthentication = null;
  });
  const refreshed = await reauthentication;
  const retryHeaders = new Headers(init.headers || {});
  retryHeaders.set('Authorization', `Bearer ${refreshed.access_token}`);
  const retried = await runtimeFetch(
    input,
    { ...init, headers: retryHeaders, credentials: init.credentials ?? 'include' },
    method,
  );
  await publishRuntimeResponse(retried, method);
  return retried;
}
